import type { ConversationsResponse, OverviewResponse } from '@platform/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CountUp,
  EChart,
  EmptyState,
  ErrorState,
  IconBadge,
  type IconBadgeProps,
  Skeleton,
  Sparkline,
  cn,
} from '@platform/ui';
import { Link } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  AtSign,
  BadgeCheck,
  BarChart3,
  Bell,
  Building2,
  CheckCircle2,
  Filter,
  MailOpen,
  MailWarning,
  MessagesSquare,
  Minus,
  Percent,
  Reply,
  ScanSearch,
  Send,
  ShieldAlert,
  Sparkles,
  Store,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { Guard, PageHeader } from '../components/page';
import { errorMessage } from '../lib/api';
import { baseChart, useChartColors } from '../lib/charts';
import { TenantLogo } from '../components/company';
import { InstantlyTotals, TotalsFootnote, TrackedRate, trackingNote } from '../components/metrics';
import { fmtDay, fmtNum, fmtPct, useScopedQuery } from '../lib/console';
import { fmtAgo, fmtRange } from '../lib/format';
import { canAccess, moduleById } from '../lib/modules';
import { useMe } from '../lib/queries';
import { useScope } from '../lib/use-scope';
import { RangePicker, useScopeLabel } from './console';

const BOUNCE_THRESHOLD = 0.04;

// ------------------------------------------------------------------ cálculo

interface Metrics {
  sent: number;
  replies: number;
  interested: number;
  newLeads: number;
  replyRate: number | null;
}

const funnelValue = (d: OverviewResponse, key: string) =>
  d.funnel.find((f) => f.key === key)?.value ?? 0;

function metricsOf(d: OverviewResponse): Metrics {
  return {
    sent: d.kpis.sentRange,
    replies: funnelValue(d, 'replied'),
    interested: d.kpis.interested,
    newLeads: funnelValue(d, 'ingested'),
    replyRate: d.kpis.replyRate,
  };
}

/**
 * Periodo anterior = (ventana doble) − (ventana actual). Exacto para envíos y leads nuevos; para
 * respuestas e interesados (personas distintas) es una muy buena aproximación.
 */
export function previousMetrics(cur: OverviewResponse, dbl: OverviewResponse): Metrics {
  const a = metricsOf(cur);
  const b = metricsOf(dbl);
  const sent = Math.max(0, b.sent - a.sent);
  const repliedCount = (d: OverviewResponse) =>
    Math.round((d.kpis.replyRate ?? 0) * d.kpis.sentRange);
  const prevReplied = Math.max(0, repliedCount(dbl) - repliedCount(cur));
  return {
    sent,
    replies: Math.max(0, b.replies - a.replies),
    interested: Math.max(0, b.interested - a.interested),
    newLeads: Math.max(0, b.newLeads - a.newLeads),
    replyRate: sent > 0 ? prevReplied / sent : null,
  };
}

/** Variación relativa; `null` si no hay base para comparar. */
export function change(cur: number | null, prev: number | null): number | null {
  if (cur === null || prev === null || prev === 0) return null;
  return (cur - prev) / prev;
}

// ------------------------------------------------------------------ piezas

function Delta({ value, days }: { value: number | null; days: number }) {
  if (value === null) {
    return <p className="text-xs text-muted-foreground">Sin datos del periodo anterior</p>;
  }
  const flat = Math.abs(value) < 0.005;
  const up = value > 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <p
      className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
      title={`Comparado con los ${String(days)} días anteriores`}
    >
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-sm px-1 font-semibold tabular-nums',
          flat
            ? 'bg-muted text-muted-foreground'
            : up
              ? 'bg-success/12 text-success'
              : 'bg-warning/15 text-warning',
        )}
      >
        <Icon className="size-3.5" aria-hidden />
        {flat ? 'igual' : `${up ? '+' : '−'}${fmtPct(Math.abs(value))}`}
      </span>
      <span className="whitespace-nowrap">vs. periodo anterior</span>
    </p>
  );
}

type Tone = NonNullable<IconBadgeProps['tone']>;

/** Color de la mini-tendencia/medidor, alineado con el tono del ícono (y con los gráficos). */
const TONE_INK: Partial<Record<Tone, string>> = {
  cyan: 'text-chart-1',
  green: 'text-chart-2',
  brand: 'text-highlight',
  success: 'text-success',
  navy: 'text-brand-navy dark:text-chart-1',
};

function Stat({
  label,
  icon: Icon,
  tone,
  value,
  format,
  delta,
  days,
  spark,
  meter,
}: {
  label: string;
  icon: LucideIcon;
  tone: Tone;
  value: number | null;
  format: (n: number) => string;
  delta: number | null;
  days: number;
  /** Serie diaria para la mini-tendencia. */
  spark?: number[];
  /** Proporción que da contexto a la cifra cuando no hay serie diaria. */
  meter?: { value: number | null; label: string };
}) {
  const ink = TONE_INK[tone] ?? 'text-chart-1';
  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border bg-card p-4 shadow-card sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <IconBadge tone={tone} size="sm">
          <Icon />
        </IconBadge>
      </div>
      <p className="font-display text-[1.75rem] leading-none font-semibold tracking-tight tabular-nums sm:text-[2rem]">
        {value === null ? '—' : <CountUp value={value} format={format} />}
      </p>
      {spark?.some((v) => v > 0) ? (
        <Sparkline values={spark} className={cn('-mx-0.5', ink)} />
      ) : meter ? (
        <div className="flex min-h-8 flex-col justify-center gap-1.5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
            <div
              className={cn('h-full rounded-full bg-current', ink)}
              style={{ width: `${String(Math.min(100, (meter.value ?? 0) * 100))}%` }}
            />
          </div>
          <p className="text-xs leading-snug text-pretty text-muted-foreground">
            {fmtPct(meter.value)} {meter.label}
          </p>
        </div>
      ) : (
        <div className="h-8" aria-hidden />
      )}
      <div className="mt-auto border-t border-dashed pt-2.5">
        <Delta value={delta} days={days} />
      </div>
    </div>
  );
}

/** Tasa de respuesta móvil de 7 días (suaviza los días sin envíos). */
function rollingRate(sent: number[], replied: number[]): number[] {
  return sent.map((_, i) => {
    const from = Math.max(0, i - 6);
    const s = sent.slice(from, i + 1).reduce((a, b) => a + b, 0);
    const r = replied.slice(from, i + 1).reduce((a, b) => a + b, 0);
    return s ? r / s : 0;
  });
}

const FUNNEL_STEPS: { key: string; label: string; help: string; icon: LucideIcon }[] = [
  { key: 'ingested', label: 'Tiendas encontradas', help: 'Nuevas en el periodo', icon: ScanSearch },
  {
    key: 'qualified',
    label: 'Encajan con tu perfil',
    help: 'Aprobadas por el análisis',
    icon: BadgeCheck,
  },
  {
    key: 'with_email',
    label: 'Con email de contacto',
    help: 'Se les puede escribir',
    icon: AtSign,
  },
  { key: 'sent', label: 'Recibieron tu email', help: 'Personas contactadas', icon: Send },
  { key: 'opened', label: 'Lo abrieron', help: '', icon: MailOpen },
  { key: 'replied', label: 'Respondieron', help: '', icon: Reply },
  { key: 'interested', label: 'Interesadas', help: 'Quieren saber más', icon: Sparkles },
];

function Funnel({ data }: { data: OverviewResponse }) {
  const opens = data.kpis.openTracking;
  // Sin seguimiento de aperturas, "Lo abrieron" siempre daría 0: la etapa no se muestra.
  const steps = FUNNEL_STEPS.filter((s) => s.key !== 'opened' || opens.status !== 'none').map(
    (s) => ({
      ...s,
      help: s.key === 'opened' ? (trackingNote(opens) ?? s.help) : s.help,
      value: funnelValue(data, s.key),
    }),
  );
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <ol className="flex flex-col gap-3" aria-label="Embudo">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1]?.value : undefined;
        const conv = prev ? s.value / prev : null;
        return (
          <li
            key={s.key}
            className="flex flex-col gap-1.5 sm:grid sm:grid-cols-[15rem_1fr] sm:items-center sm:gap-3"
          >
            <div className="flex min-w-0 items-center gap-3">
              <IconBadge
                size="sm"
                tone={s.key === 'interested' ? 'green' : i < 3 ? 'navy' : 'cyan'}
              >
                <s.icon />
              </IconBadge>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{s.label}</p>
                {conv !== null && conv <= 1 ? (
                  <p className="text-xs text-muted-foreground">{fmtPct(conv)} del paso anterior</p>
                ) : s.help ? (
                  <p className="text-xs text-muted-foreground">{s.help}</p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-6 flex-1 overflow-hidden rounded-full bg-muted/80 ring-1 ring-border/50 ring-inset">
                <div
                  className={cn(
                    'h-full rounded-full shadow-[inset_0_1px_0_rgb(255_255_255/0.25)] transition-[width] duration-500',
                    s.key === 'interested'
                      ? 'bg-chart-2'
                      : i < 3
                        ? 'bg-brand-navy/80 dark:bg-chart-1/60'
                        : 'bg-chart-1',
                  )}
                  style={{
                    width: `${String(Math.max(s.value > 0 ? 2 : 0, (s.value / max) * 100))}%`,
                  }}
                />
              </div>
              <span className="w-12 text-right font-display text-base font-semibold tabular-nums">
                {fmtNum(s.value)}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function AttentionItem({
  icon,
  tone,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  tone: 'warning' | 'destructive' | 'info';
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <li className="flex gap-3 rounded-lg border bg-background/40 p-3">
      <IconBadge tone={tone === 'info' ? 'brand' : tone} className="mt-0.5">
        {icon}
      </IconBadge>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm font-medium">{title}</p>
        {body ? <div className="text-sm text-muted-foreground">{body}</div> : null}
        {action ? <div className="pt-1">{action}</div> : null}
      </div>
    </li>
  );
}

function Attention({ data }: { data: OverviewResponse }) {
  const me = useMe();
  const { scope } = useScope();
  const canInbox = canAccess(me, 'inbox:read');
  const canOps = canAccess(me, 'ops:read');
  const inbox = useScopedQuery<ConversationsResponse>(
    ['inbox', 'open', 'preview'],
    '/api/inbox/conversations?status=open&pageSize=3',
    {
      requires: 'inbox:read',
      enabled: canInbox,
    },
  );
  const pending = inbox.data?.counts.open ?? 0;
  const bounce = data.kpis.bounceRate;
  const items: ReactNode[] = [];
  if (pending > 0) {
    items.push(
      <AttentionItem
        key="inbox"
        icon={<MessagesSquare />}
        tone="warning"
        title={
          pending === 1
            ? '1 conversación espera tu respuesta'
            : `${fmtNum(pending)} conversaciones esperan tu respuesta`
        }
        body={
          <ul className="flex flex-col gap-1">
            {(inbox.data?.rows ?? []).slice(0, 3).map((it) => (
              <li key={it.id} className="truncate">
                <span className="font-medium text-foreground">{it.company ?? it.leadEmail}</span> ·{' '}
                {fmtAgo(it.lastMessageAt)}
              </li>
            ))}
          </ul>
        }
        action={
          <Button asChild size="sm">
            <Link to={moduleById('inbox').path} search={{ scope }}>
              Responder ahora <ArrowRight />
            </Link>
          </Button>
        }
      />,
    );
  }
  if (bounce !== null && bounce > BOUNCE_THRESHOLD) {
    items.push(
      <AttentionItem
        key="bounce"
        icon={<MailWarning />}
        tone="destructive"
        title={`Rebotan demasiados emails (${fmtPct(bounce)})`}
        body="Lo recomendable es quedar por debajo del 4 %. Si sigue subiendo, los envíos se pausan solos para proteger tus buzones."
      />,
    );
  }
  if (canOps && data.kpis.openAlerts > 0) {
    items.push(
      <AttentionItem
        key="alerts"
        icon={<Bell />}
        tone="info"
        title={
          data.kpis.openAlerts === 1
            ? '1 aviso técnico abierto'
            : `${fmtNum(data.kpis.openAlerts)} avisos técnicos abiertos`
        }
        action={
          <Button asChild size="sm" variant="outline">
            <Link to={moduleById('alerts').path} search={{ scope }}>
              Ver avisos
            </Link>
          </Button>
        }
      />,
    );
  }
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle icon={<Bell />}>Qué requiere tu atención</CardTitle>
      </CardHeader>
      <CardContent>
        {inbox.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : items.length > 0 ? (
          <ul className="flex flex-col gap-3">{items}</ul>
        ) : (
          <div className="flex items-start gap-3 rounded-lg border border-success/20 bg-success/8 p-4">
            <IconBadge tone="success">
              <CheckCircle2 />
            </IconBadge>
            <div>
              <p className="text-sm font-medium">Todo en orden</p>
              <p className="text-sm text-muted-foreground">
                No hay respuestas pendientes ni avisos. Te lo diremos aquí cuando algo necesite una
                persona.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DailyChart({
  title,
  description,
  dates,
  values,
  color,
  name,
  unit,
  icon,
}: {
  title: string;
  description: string;
  dates: string[];
  values: number[];
  color: 0 | 1;
  name: string;
  unit: string;
  icon: ReactNode;
}) {
  const { theme, colors } = useChartColors();
  const base = baseChart(colors);
  const option = {
    ...base,
    grid: { left: 36, right: 12, top: 12, bottom: 24 },
    xAxis: { ...base.xAxis, data: dates.map(fmtDay) },
    series: [
      {
        type: 'bar',
        name,
        data: values,
        barMaxWidth: 12,
        barCategoryGap: '38%',
        itemStyle: { color: colors.series[color], borderRadius: [6, 6, 2, 2] },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
    ],
  };
  const total = values.reduce((a, b) => a + b, 0);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle icon={icon}>{title}</CardTitle>
          <CardDescription className="mt-1.5">{description}</CardDescription>
        </div>
        <p className="shrink-0 text-right">
          <span className="block font-display text-2xl leading-none font-semibold tabular-nums">
            {fmtNum(total)}
          </span>
          <span className="text-xs text-muted-foreground">{unit}</span>
        </p>
      </CardHeader>
      <CardContent>
        <EChart
          theme={theme}
          option={option}
          className="h-52"
          aria-label={`${title}: gráfico de barras por día`}
          role="img"
        />
      </CardContent>
    </Card>
  );
}

function TenantCards({ data }: { data: OverviewResponse }) {
  const { setScope } = useScope();
  const max = Math.max(1, ...data.byTenant.map((t) => t.sent));
  return (
    <section aria-labelledby="por-empresa" className="mt-8">
      <div className="mb-4 flex items-center gap-3">
        <IconBadge tone="navy">
          <Building2 />
        </IconBadge>
        <div>
          <h2 id="por-empresa" className="text-lg leading-tight font-semibold">
            Por empresa
          </h2>
          <p className="text-sm text-muted-foreground">Toca una empresa para ver solo sus datos.</p>
        </div>
      </div>
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.byTenant.map((t) => {
          const replyRate = t.sent ? t.replied / t.sent : null;
          const bounce = t.sent ? t.bounced / t.sent : null;
          return (
            <li key={t.tenantId}>
              <button
                type="button"
                onClick={() => {
                  setScope(`tenant:${t.slug}`);
                }}
                className="flex h-full w-full flex-col gap-4 rounded-xl border bg-card p-5 text-left shadow-card transition-[transform,box-shadow,border-color] duration-200 outline-none hover:-translate-y-0.5 hover:border-highlight/40 hover:shadow-raised focus-visible:ring-2 focus-visible:ring-ring active:translate-y-0"
              >
                <div className="flex w-full items-center gap-3">
                  <TenantLogo name={t.name} slug={t.slug} />
                  <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
                    <span className="font-display text-base leading-tight font-semibold">
                      {t.name}
                    </span>
                    {t.sent === 0 ? (
                      <Badge variant="secondary">Sin envíos</Badge>
                    ) : bounce !== null && bounce > BOUNCE_THRESHOLD ? (
                      <Badge variant="destructive">Rebote alto</Badge>
                    ) : (
                      <Badge variant="success">Enviando</Badge>
                    )}
                  </span>
                </div>
                <dl className="flex w-full justify-between gap-3 rounded-lg bg-muted/50 p-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Enviados</dt>
                    <dd className="font-display text-lg font-semibold tabular-nums">
                      {fmtNum(t.sent)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Respuestas</dt>
                    <dd className="font-display text-lg font-semibold tabular-nums">
                      {fmtNum(t.replied)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Tasa resp.</dt>
                    <dd className="font-display text-lg font-semibold tabular-nums">
                      {fmtPct(replyRate)}
                    </dd>
                  </div>
                </dl>
                <div className="w-full">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
                    <div
                      className="h-full rounded-full bg-chart-1"
                      style={{ width: `${String((t.sent / max) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {fmtNum(t.leads)} leads en total · rebote {fmtPct(bounce)}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function OverviewSkeleton() {
  return (
    <div aria-busy="true" aria-label="Cargando" className="flex flex-col gap-6">
      <Skeleton className="h-36 w-full rounded-2xl" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-44 rounded-xl" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-80 rounded-xl lg:col-span-2" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ página

export function OverviewPage() {
  const me = useMe();
  const { scope } = useScope();
  const label = useScopeLabel();
  const [days, setDays] = useState(30);
  const q = useScopedQuery<OverviewResponse>(
    ['overview', days],
    `/api/overview?days=${String(days)}`,
  );
  const dbl = useScopedQuery<OverviewResponse>(
    ['overview', days * 2],
    `/api/overview?days=${String(days * 2)}`,
  );
  const data = q.data;
  const cur = data ? metricsOf(data) : null;
  const prev = data && dbl.data ? previousMetrics(data, dbl.data) : null;
  const firstName = me.user.name.split(' ')[0] ?? me.user.name;
  const nothing = data?.kpis.sentRange === 0 && data.funnel.every((f) => f.value === 0);

  return (
    <Guard requires="data:read">
      <PageHeader
        title="Inicio"
        description={data ? `${label} · ${fmtRange(data.range.from, data.range.to)}` : label}
        actions={<RangePicker days={days} onChange={setDays} />}
      />
      {q.isLoading ? (
        <OverviewSkeleton />
      ) : q.error ? (
        <ErrorState message={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : data && cur ? (
        nothing ? (
          <EmptyState
            icon={<Send />}
            title="Todavía no hay actividad en este periodo"
            description="Cuando la campaña empiece a enviar emails verás aquí cuántos salen, quién responde y quién está interesado. Mientras tanto puedes revisar los leads que ya se encontraron."
            action={
              <Button asChild>
                <Link to={moduleById('leads').path} search={{ scope }}>
                  Ver leads
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <section
              aria-label="Resumen"
              className="relative mb-6 overflow-hidden rounded-2xl bg-brand-navy px-5 py-6 text-white shadow-raised ring-1 ring-white/5 sm:px-8 sm:py-7"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-circuit opacity-[0.16] mask-l-from-10% mask-l-to-75%"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-brand-cyan/15 blur-3xl"
              />
              <p className="relative text-sm text-white/75">
                Hola, {firstName}. Así van las cosas:
              </p>
              <p className="relative mt-2 max-w-3xl font-display text-xl leading-snug font-medium sm:text-2xl">
                En los últimos {days} días se enviaron{' '}
                <strong className="font-semibold text-brand-cyan">{fmtNum(cur.sent)} emails</strong>{' '}
                y respondieron{' '}
                <strong className="font-semibold text-brand-cyan">
                  {fmtNum(cur.replies)} {cur.replies === 1 ? 'persona' : 'personas'}
                </strong>
                {cur.interested > 0 ? (
                  <>
                    ;{' '}
                    <strong className="font-semibold text-brand-green">
                      {fmtNum(cur.interested)}
                    </strong>{' '}
                    {cur.interested === 1 ? 'está interesada' : 'están interesadas'}
                  </>
                ) : null}
                .
              </p>
              <dl className="relative mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-sm">
                <div className="flex items-center gap-2">
                  <MailOpen className="size-4 text-brand-cyan" aria-hidden />
                  <dt className="text-white/75">Abren tu email</dt>
                  <dd className="font-display font-semibold tabular-nums">
                    <TrackedRate
                      rate={data.kpis.openRate}
                      tracking={data.kpis.openTracking}
                      kind="opens"
                      noteClassName="font-sans text-white/65"
                    />
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldAlert
                    className={cn(
                      'size-4',
                      (data.kpis.bounceRate ?? 0) > BOUNCE_THRESHOLD
                        ? 'text-amber-300'
                        : 'text-brand-green',
                    )}
                    aria-hidden
                  />
                  <dt className="text-white/75">Rebotan</dt>
                  <dd className="font-display font-semibold tabular-nums">
                    {fmtPct(data.kpis.bounceRate)}{' '}
                    <span className="font-sans font-normal text-white/65">(límite 4 %)</span>
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <Store className="size-4 text-brand-cyan" aria-hidden />
                  <dt className="text-white/75">En cola para escribirles</dt>
                  <dd className="font-display font-semibold tabular-nums">
                    {fmtNum(data.kpis.leadsQueued)}
                  </dd>
                </div>
              </dl>
            </section>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
              <Stat
                label="Emails enviados"
                icon={Send}
                tone="cyan"
                value={cur.sent}
                format={fmtNum}
                spark={data.trend.map((t) => t.sent)}
                delta={change(cur.sent, prev?.sent ?? null)}
                days={days}
              />
              <Stat
                label="Respuestas"
                icon={Reply}
                tone="green"
                value={cur.replies}
                format={fmtNum}
                spark={data.trend.map((t) => t.replied)}
                delta={change(cur.replies, prev?.replies ?? null)}
                days={days}
              />
              <Stat
                label="Interesados"
                icon={Sparkles}
                tone="success"
                value={cur.interested}
                format={fmtNum}
                meter={{
                  value: cur.replies ? cur.interested / cur.replies : null,
                  label: 'de quienes respondieron',
                }}
                delta={change(cur.interested, prev?.interested ?? null)}
                days={days}
              />
              <Stat
                label="Tasa de respuesta"
                icon={Percent}
                tone="brand"
                value={cur.replyRate}
                format={(n) => fmtPct(n)}
                spark={rollingRate(
                  data.trend.map((t) => t.sent),
                  data.trend.map((t) => t.replied),
                )}
                delta={change(cur.replyRate, prev?.replyRate ?? null)}
                days={days}
              />
              <div className="col-span-2 lg:col-span-1">
                <Stat
                  label="Leads nuevos"
                  icon={Store}
                  tone="navy"
                  value={cur.newLeads}
                  format={fmtNum}
                  meter={{
                    value: cur.newLeads ? funnelValue(data, 'qualified') / cur.newLeads : null,
                    label: 'encajan con tu perfil',
                  }}
                  delta={change(cur.newLeads, prev?.newLeads ?? null)}
                  days={days}
                />
              </div>
            </div>

            {data.instantly.sequencesStarted > 0 ? (
              <section aria-labelledby="instantly-totals" className="mt-6">
                <h2 id="instantly-totals" className="mb-3 text-base font-semibold">
                  Tus campañas, como las ve Instantly
                </h2>
                <InstantlyTotals totals={data.instantly} />
                <TotalsFootnote updatedAt={data.instantly.updatedAt} />
              </section>
            ) : null}

            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle icon={<Filter />}>Del lead a la respuesta</CardTitle>
                  <CardDescription>
                    Cuántas tiendas pasan cada etapa en los últimos {days} días.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Funnel data={data} />
                </CardContent>
              </Card>
              <Attention data={data} />
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <DailyChart
                title="Emails enviados por día"
                description={`Hoy: ${fmtNum(data.kpis.sentToday)} · últimos 7 días: ${fmtNum(data.kpis.sent7d)}`}
                dates={data.trend.map((t) => t.date)}
                values={data.trend.map((t) => t.sent)}
                color={0}
                name="Enviados"
                unit="emails"
                icon={<BarChart3 />}
              />
              <DailyChart
                title="Respuestas recibidas por día"
                description="Mensajes recibidos, sin contar los automáticos (fuera de oficina)"
                dates={data.trend.map((t) => t.date)}
                values={data.trend.map((t) => t.replied)}
                color={1}
                name="Respuestas"
                unit="mensajes"
                icon={<MessagesSquare />}
              />
            </div>

            {data.byTenant.length > 1 ? <TenantCards data={data} /> : null}
          </>
        )
      ) : null}
    </Guard>
  );
}
